import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  Inject,
  PLATFORM_ID,
} from "@angular/core";
import { RouterLink, RouterModule } from "@angular/router";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { ThemeService } from "../../services/theme.service";
import { BackdropComponent } from "../../shared/components/backdrop/backdrop.component";
import { AuthService } from "../../services/auth.service";
import { PortfolioService } from "../../services/portfolio.service";
import { SemanticJobSearchService } from "../../services/semantic-job-search.service";
import { Router } from "@angular/router";
import { Subject, Observable, combineLatest, from } from "rxjs";
import { takeUntil, tap, switchMap, filter, exhaustMap } from "rxjs/operators";
import { JobProgressService } from "../../services/job-progress.service";
import { DailyQuotesService } from "../../services/daily-quotes.service";
import { of } from "rxjs";
import { HugeiconsIconComponent } from "@hugeicons/angular";
import {
  Home01Icon,
  Briefcase01Icon,
  ClipboardIcon,
  Notification01Icon,
  ArrowDown01Icon,
  Moon02Icon,
  Sun02Icon,
  Cancel01Icon,
  CustomerService01Icon,
  LogoutSquare01Icon,
  Menu01Icon,
  UserCircleIcon,
} from "@hugeicons/core-free-icons";

@Component({
  selector: "app-header",
  standalone: true,
  imports: [
    RouterLink,
    RouterModule,
    CommonModule,
    BackdropComponent,
    HugeiconsIconComponent,
  ],
  templateUrl: "./header.component.html",
  styles: [
    `
      /* Navigation Dropdown Styles */
      .nav-item {
        position: relative;
        display: flex;
        align-items: center;
      }


      .nav-dropdown {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 8px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 16px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05);
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.2s, visibility 0.2s, transform 0.2s;
        transform: translateX(-50%) translateY(-8px);
        z-index: 50;
      }

      :host-context(.dark) .nav-dropdown {
        background: #1f2937;
        border-color: #374151;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      .nav-dropdown.active {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: translateX(-50%) translateY(0);
      }

      .nav-dropdown-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
        padding: 12px;
      }

      .nav-dropdown-item {
        display: block;
        padding: 12px;
        border-radius: 10px;
        transition: background-color 0.2s;
        text-decoration: none;
      }

      .nav-dropdown-item:hover {
        background: #f0f5f0;
      }

      :host-context(.dark) .nav-dropdown-item:hover {
        background: #374151;
      }

      .nav-dropdown-item-title {
        font-size: 16px;
        font-weight: 700;
        color: #111827;
        margin-bottom: 2px;
      }

      :host-context(.dark) .nav-dropdown-item-title {
        color: #ffffff;
      }

      .nav-dropdown-item-description {
        font-size: 13px;
        color: #6b7280;
      }

      :host-context(.dark) .nav-dropdown-item-description {
        color: #9ca3af;
      }

      /* Hover styles for Find Work and Find Talent dropdown items */
      .nav-dropdown a.flex.items-center.group:hover {
        background: #f0f5f0 !important;
      }

      :host-context(.dark) .nav-dropdown a.flex.items-center.group:hover {
        background: #374151 !important;
      }

      /* Hover styles for other dropdown items that don't have the nav-dropdown-item class */
      .nav-dropdown .flex.items-center:hover {
        background: #f0f5f0;
      }

      :host-context(.dark) .nav-dropdown .flex.items-center:hover {
        background: #374151;
      }

      /* Floating Glass Side Nav Styles */
      .side-glass-nav {
        background: rgba(255, 255, 255, 0.42);
        backdrop-filter: blur(24px) saturate(1.4);
        -webkit-backdrop-filter: blur(24px) saturate(1.4);
        border: 1px solid rgba(255, 255, 255, 0.55);
        border-radius: 22px;
        box-shadow: 0 8px 40px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04),
          inset 0 1px 0 rgba(255, 255, 255, 0.5);
        padding: 20px 14px;
      }

      :host-context(.dark) .side-glass-nav {
        background: rgba(32, 41, 57, 0.6);
        border: 1px solid var(--dark-gray-700, #4b5565);
        box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25), 0 1px 3px rgba(0, 0, 0, 0.15);
      }

      .sg-item.sg-item-active {
        background: rgba(255, 255, 255, 0.7);
        color: #1a1a1a;
        font-weight: 500;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
      }

      :host-context(.dark) .sg-item.sg-item-active {
        background: var(--dark-gray-700, #4b5565);
        color: var(--dark-gray-50, #fcfcfd);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      }

      .sg-item.sg-item-active hugeicons-icon,
      .sg-item.sg-item-active .nav-icon-rainbow {
        opacity: 1 !important;
      }

      .avatar-shimmer {
        --cl-shimmer-hover-transform: skew(-45deg) translateX(600%);
        --cl-shimmer-hover-after-transform: skewX(45deg) translateX(-150%);
        position: relative;
        overflow: hidden;
        transition-property: background-color, background, border-color, color,
          fill, stroke, opacity, box-shadow, transform;
        transition-duration: 100ms;
      }

      .avatar-shimmer::before {
        content: "";
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.4),
          transparent
        );
        transition: transform 0.6s ease;
        transform: skewX(-45deg) translateX(-200%);
      }

      .avatar-shimmer:hover::before,
      .user-menu-trigger:hover .avatar-shimmer::before {
        transform: var(--cl-shimmer-hover-transform);
      }

      .avatar-shimmer::after {
        content: "";
        position: absolute;
        top: 0;
        left: -100%;
        width: 50%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.2),
          transparent
        );
        transition: transform 0.6s ease 0.1s;
        transform: skewX(45deg) translateX(-300%);
      }

      .avatar-shimmer:hover::after,
      .user-menu-trigger:hover .avatar-shimmer::after {
        transform: var(--cl-shimmer-hover-after-transform);
      }

      /* Profile card preview hover animations (match homepage Custom profile card) */
      @keyframes headerBounceIn {
        0% { opacity: 0; transform: scale(0.6); }
        60% { transform: scale(1.06); }
        100% { opacity: 1; transform: scale(1); }
      }
      @keyframes headerFadeSlideUp {
        0% { opacity: 0; transform: translateY(18px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes headerPopIn {
        0% { opacity: 0; transform: translateY(14px) scale(0.97); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes headerBadgeSpin {
        0% { opacity: 0; transform: scale(0) rotate(-90deg); }
        100% { opacity: 1; transform: scale(1) rotate(0); }
      }
      .create-profile-card-link:hover .profile-preview-avatar-wrap {
        animation: headerBounceIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.05s
          both;
      }
      .create-profile-card-link:hover .profile-preview-name {
        animation: headerFadeSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.2s
          both;
      }
      .create-profile-card-link:hover .profile-preview-loc {
        animation: headerFadeSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.28s
          both;
      }
      .create-profile-card-link:hover .profile-preview-bio {
        animation: headerFadeSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.36s
          both;
      }
      .create-profile-card-link:hover .profile-preview-badge {
        animation: headerPopIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.44s both;
      }
      .create-profile-card-link:hover .profile-preview-verified {
        animation: headerBadgeSpin 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.3s
          both;
      }

      /* Side nav icon rainbow (gradient on icon only, like homepage category mask) */
      .nav-icon-rainbow {
        position: relative;
        display: inline-block;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        background-color: currentColor;
        mask-image: var(--icon-mask);
        -webkit-mask-image: var(--icon-mask);
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
        pointer-events: none;
      }
      .nav-icon-rainbow::before,
      .nav-icon-rainbow::after {
        content: "";
        position: absolute;
        inset: 0;
        mask-image: var(--icon-mask);
        -webkit-mask-image: var(--icon-mask);
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
        opacity: 0;
        pointer-events: none;
      }
      .nav-icon-rainbow::before {
        background: linear-gradient(
          90deg,
          #ff005d,
          #ff7a00,
          #ffe600,
          #2dff8f,
          #00c2ff,
          #7a5cff,
          #ff00c8,
          #ff005d
        );
        background-size: 300% 300%;
        filter: saturate(1.4) contrast(1.05);
      }
      .nav-icon-rainbow::after {
        background: linear-gradient(
          120deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0) 38%,
          rgba(255, 255, 255, 0.9) 50%,
          rgba(255, 255, 255, 0) 62%,
          rgba(255, 255, 255, 0) 100%
        );
        transform: translateX(-140%);
        filter: blur(0.2px);
      }
      .nav-icon-rainbow--animate::before {
        opacity: 1;
        animation: headerNavRainbowBurst 950ms ease-out forwards;
      }
      .nav-icon-rainbow--animate::after {
        opacity: 1;
        animation: headerNavShine 650ms ease-out 120ms forwards;
      }
      @keyframes headerNavRainbowBurst {
        0% {
          opacity: 1;
          background-position: 0% 50%;
          transform: scale(0.98);
        }
        55% {
          opacity: 1;
          background-position: 100% 50%;
          transform: scale(1);
        }
        100% {
          opacity: 0;
          background-position: 100% 50%;
          transform: scale(1);
        }
      }
      @keyframes headerNavShine {
        0% {
          opacity: 0;
          transform: translateX(-140%);
        }
        20% {
          opacity: 0.85;
        }
        100% {
          opacity: 0;
          transform: translateX(140%);
        }
      }
    `,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  showFindWorkMenu = false;
  showFindTalentMenu = false;
  showBlogMenu = false;
  showUserMenu = false;
  isMobileMenuOpen = false;
  activeMobileSubmenu: string | null = null;

  // Navigation background animation properties
  navBackgroundVisible = false;
  navBackgroundStyle = {
    left: "0px",
    width: "0px",
  };

  // Progress bar observables
  progressBarVisible$: Observable<boolean>;
  progressPercentage$: Observable<number>;

  // Track job matches count
  jobMatchesCount = 0;
  private destroy$ = new Subject<void>();

  // Hugeicons for nav (see https://hugeicons.com/docs/)
  icons = {
    home: Home01Icon,
    briefcase: Briefcase01Icon,
    clipboard: ClipboardIcon,
    notification: Notification01Icon,
    arrowDown: ArrowDown01Icon,
    moon: Moon02Icon,
    sun: Sun02Icon,
    cancel: Cancel01Icon,
    help: CustomerService01Icon,
    logout: LogoutSquare01Icon,
    menu: Menu01Icon,
    user: UserCircleIcon,
  };

  // Talent profile status
  hasProfile = false;
  isCheckingProfile = false;

  isHeaderVisible = true;
  lastScrollTop = 0;
  scrollThreshold = 50;

  private isBrowser: boolean;

  constructor(
    public themeService: ThemeService,
    public authService: AuthService,
    private semanticJobSearchService: SemanticJobSearchService,
    private portfolioService: PortfolioService,
    private router: Router,
    private jobProgressService: JobProgressService,
    public dailyQuotesService: DailyQuotesService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    // Initialize observables from the service
    this.progressBarVisible$ = this.jobProgressService.isVisible$;
    this.progressPercentage$ = this.jobProgressService.progress$;
  }

  ngOnInit(): void {
    // Subscribe to job matches count (semantic search flow)
    this.semanticJobSearchService.jobMatchesCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.jobMatchesCount = count;
      });

    // Restore matches count from cache when 0 (e.g. user opened job-details first)
    combineLatest([
      this.semanticJobSearchService.jobMatchesCount$,
      this.authService.currentUser$,
    ])
      .pipe(
        filter(([count, user]) => count === 0 && !!user?.id),
        exhaustMap(([, user]) =>
          from(this.semanticJobSearchService.getCacheStatus(user!.id))
        ),
        filter((status) => status.totalJobs > 0),
        takeUntil(this.destroy$)
      )
      .subscribe((status) => {
        this.semanticJobSearchService.setJobMatchesCount(status.totalJobs);
      });

    // Subscribe to user changes and check profile status
    this.authService.currentUser$
      .pipe(
        tap((user) => {
          // User data loaded
        }),
        switchMap((user) => {
          if (user?.id) {
            this.isCheckingProfile = true;
            return this.portfolioService.getTalentProfile(user.id);
          } else {
            this.hasProfile = false;
            this.isCheckingProfile = false;
            return of(null);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (profile) => {
          this.hasProfile = !!profile;
          this.isCheckingProfile = false;
        },
        error: (error) => {
          this.hasProfile = false;
          this.isCheckingProfile = false;
        },
      });

    // Initialize scroll position and header visibility
    if (this.isBrowser) {
      this.lastScrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      // Set initial header visibility based on scroll position
      this.isHeaderVisible = this.lastScrollTop <= this.scrollThreshold;
    }
  }

  @HostListener("window:scroll", [])
  onWindowScroll(): void {
    if (!this.isBrowser) return;
    
    // Ignore programmatic scrolls (e.g., from job card expansion)
    // Also check if we're within 1 second of a programmatic scroll start (for mobile smooth scrolling)
    const isProgrammatic = (window as any).isProgrammaticScroll;
    const scrollStartTime = (window as any).programmaticScrollStartTime;
    const isWithinProgrammaticWindow = scrollStartTime && (Date.now() - scrollStartTime < 1000);
    
    if (isProgrammatic || isWithinProgrammaticWindow) {
      return;
    }

    const currentScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;

    // Show header at the top of the page
    if (currentScrollTop <= this.scrollThreshold) {
      this.isHeaderVisible = true;
      this.lastScrollTop = currentScrollTop;
      return;
    }

    // Calculate scroll direction with a small threshold to prevent jitter
    const scrollDifference = Math.abs(currentScrollTop - this.lastScrollTop);

    // Only update if there's a significant scroll movement (prevents jitter)
    if (scrollDifference > 10) {
      if (currentScrollTop > this.lastScrollTop) {
        // Scrolling down - hide header
        this.isHeaderVisible = false;
      } else {
        // Scrolling up - show header
        this.isHeaderVisible = true;
      }

      this.lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
    }
  }

  // Method to handle programmatic scrolling
  setProgrammaticScroll(scrolling: boolean): void {
    if (scrolling) {
      // When programmatic scrolling starts, hide the header
      this.isHeaderVisible = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Close all menus except the one being opened
  openMenu(menu: "findWork" | "findTalent" | "blog") {
    if (!this.isBrowser) return;
    
    this.showFindWorkMenu = menu === "findWork";
    this.showFindTalentMenu = menu === "findTalent";
    this.showBlogMenu = menu === "blog";
    this.showUserMenu = false;
  }

  // Close all menus
  closeAllMenus() {
    if (!this.isBrowser) return;
    
    this.showFindWorkMenu = false;
    this.showFindTalentMenu = false;
    this.showBlogMenu = false;
    this.showUserMenu = false;
  }

  // Toggle user dropdown menu
  toggleUserMenu(event: Event) {
    event.stopPropagation();
    this.showUserMenu = !this.showUserMenu;
    // Close other menus
    this.showFindWorkMenu = false;
    this.showFindTalentMenu = false;
    this.showBlogMenu = false;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.activeMobileSubmenu = null;
    }
    // Prevent body scroll when menu is open
    if (this.isBrowser) {
      document.body.style.overflow = this.isMobileMenuOpen ? "hidden" : "";
    }
  }

  toggleMobileSubmenu(menu: string) {
    this.activeMobileSubmenu = this.activeMobileSubmenu === menu ? null : menu;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.activeMobileSubmenu = null;
    if (this.isBrowser) {
      document.body.style.overflow = "";
    }
  }

  login(event: Event): void {
    event.preventDefault();

    // Check if user is already authenticated
    if (this.authService.isAuthenticated()) {
      // If already logged in, navigate to dashboard directly
      this.router.navigate(["/dashboard"]);
    } else {
      // If not logged in, set the redirect path and open the login modal
      const currentUrl = this.router.url;
      this.authService.setRedirectPath(currentUrl);
      this.authService.openLoginModal();
    }
  }

  register(event: Event): void {
    event.preventDefault();

    // Check if user is already authenticated
    if (this.authService.isAuthenticated()) {
      // If already logged in, navigate to dashboard directly
      this.router.navigate(["/dashboard"]);
    } else {
      // If not logged in, set the redirect path and open the login modal
      const currentUrl = this.router.url;
      this.authService.setRedirectPath(currentUrl);
      this.authService.openLoginModal();
    }
  }

  logout() {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(["/"]);
      },
      error: (error) => {
        // Handle logout error silently
      },
    });
  }

  navigateToProfile(): void {
    // Check if user is authenticated
    if (this.authService.isAuthenticated()) {
      // If authenticated, navigate to profile
      this.router.navigate(["/profile"]);
    } else {
      // If not authenticated, set redirect path and open login modal
      this.authService.setRedirectPath("/profile");
      this.authService.openLoginModal();
    }

    // Close all menus
    this.closeAllMenus();
    this.closeMobileMenu();
  }

  handleCreateProfile(event: Event): void {
    event.preventDefault();
    this.navigateToProfile();
  }

  // Handle broken avatar image links
  handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement && imgElement.src.includes("googleusercontent.com")) {
      // Remove any size parameters if present
      if (imgElement.src.includes("=s")) {
        imgElement.src = imgElement.src.split("=")[0];
        return;
      }
      // If that doesn't work, hide the image
      imgElement.style.display = "none";
    }
  }

  // Sanitize avatar URL before displaying
  getAvatarUrl(url: string | undefined): string {
    if (!url) return "";

    // For Google avatar URLs, just ensure HTTPS and return as is
    if (url.includes("googleusercontent.com")) {
      return url.replace(/^http:/, "https:");
    }

    return url;
  }

  // Navigation background animation methods
  onNavItemHover(event: MouseEvent): void {
    if (!this.isBrowser || !event || !event.target) return;
    
    const target = event.target as HTMLElement;
    if (!target || !target.closest) return;
    
    const navItem = target.closest(".nav-item") as HTMLElement;

    if (navItem) {
      // Get the button element for precise measurements
      const button = navItem.querySelector(".nav-trigger") as HTMLElement;

      if (button && typeof button.offsetWidth !== 'undefined' && typeof navItem.offsetLeft !== 'undefined') {
        // Use offsetLeft and offsetWidth for more accurate positioning
        const leftOffset = navItem.offsetLeft;
        const buttonWidth = button.offsetWidth;

        this.navBackgroundVisible = true;
        this.navBackgroundStyle = {
          left: `${leftOffset}px`,
          width: `${buttonWidth}px`,
        };
      }
    }
  }

  onNavMenuLeave(): void {
    this.navBackgroundVisible = false;
  }
}